/**
 * screens/ruleBuilder/RuleBuilder.tsx — guided authoring with a live
 * deterministic preview. The rule is authored as a sentence with inline token
 * controls; the preview re-runs POST /v1/rules/preview on every change. UI
 * only — save + preview go through the rules service.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { LightColors as C, Radii as R, FontSize as FS, FontWeight as FW } from '../../theme/theme';
import { useResponsive } from '../../lib/responsive';
import { Txt, Row, Stack, Cluster, Divider } from '../../components/ui';
import { Card, CardHead, CardBody } from '../../components/card';
import Button from '../../components/button';
import ConfirmDialog from '../../components/confirmDialog';
import SuccessDialog from '../../components/successDialog';
import Chip from '../../components/chip';
import Switch from '../../components/switch';
import Alert from '../../components/alert';
import Spinner from '../../components/spinner';
import Icon from '../../components/icon';
import TextField, { TokenNumber } from '../../components/input';
import Select, { Option } from '../../components/select';
import FilterChip from '../../components/filterChip';
import { Page, PageHeader } from '../_layout';

import { BuilderDraft, builderSchema, Tone } from '../../schema/rules/rule';
import { PreviewResult, DROP_LABEL } from '../../schema/preview';
import { listRules, createRule, updateRule, previewRule } from '../../services/rules/rules';
import { getCapabilities } from '../../services/capabilities/capabilities';
import { Capabilities } from '../../schema/capability';
import { MOTION_OPTIONS, STRATEGY_LABELS, STRATEGY_SHORT } from '../../domain/motions';
import { Seed } from '../../services/_demo/seed';
import { getDemoVertical } from '../../services/_demo/verticalStore';
import { useToast } from '../../components/toast';
import { handleApiError } from '../../utils/errorHandler';
import { useCounts } from '../../context/countsContext';
import { useApp } from '../../context/appContext';
import type { AppStackParamList } from '../../navigation/types';

type Nav = NativeStackNavigationProp<AppStackParamList>;
type BuilderRoute = RouteProp<AppStackParamList, 'RuleBuilder'>;

const TONES: Tone[] = ['warm', 'neutral', 'playful'];

function blankDraft(): BuilderDraft {
  const cfg = Seed.config(getDemoVertical());
  return {
    id: null,
    name: '',
    motion: 'complement',
    status: 'draft',
    source: 'manual',
    priority: 90,
    triggerCat: cfg.defaults.triggerCat,
    excludeCat: cfg.defaults.excludeCat,
    fromValue: cfg.defaults.fromValue,
    select: 'curated_order',
    maxSuggestions: 1,
    maxPriceDelta: 60,
    mustAvailable: true,
    dietaryMatch: true,
    phrasing: cfg.phrasing,
    tone: 'warm',
    isNew: true,
  };
}

const fmtPrice = (p?: number) => (p == null ? '' : `₹${p}`);

export default function RuleBuilder() {
  const nav = useNavigation<Nav>();
  const route = useRoute<BuilderRoute>();
  const ruleId = route.params?.ruleId;
  const seed = route.params?.seed;
  const toast = useToast();
  const counts = useCounts();
  const { vertical } = useApp();
  const { isDesktop } = useResponsive();

  const cfg = Seed.config(vertical);

  const [caps, setCaps] = useState<Capabilities | null>(null);
  // Prefer the tenant's REAL catalog categories (from /v1/capabilities); fall
  // back to the seed categories only when offline/demo (caps has no cats).
  const catOptions: Option[] = caps?.cats && caps.cats.length > 0 ? caps.cats : cfg.cats;

  // A proposal opened via "Edit" arrives as a seed — fold its fields onto a
  // fresh draft so the Builder shows the suggestion's real shape, not defaults.
  const withSeed = useCallback(
    (base: BuilderDraft): BuilderDraft => {
      if (!seed) return base;
      return {
        ...base,
        ...(seed.motion ? { motion: seed.motion } : {}),
        ...(seed.triggerCat ? { triggerCat: seed.triggerCat } : {}),
        ...(seed.excludeCat ? { excludeCat: seed.excludeCat } : {}),
        ...(seed.fromValue ? { fromValue: seed.fromValue } : {}),
        ...(seed.select ? { select: seed.select } : {}),
        ...(seed.maxPriceDelta != null ? { maxPriceDelta: seed.maxPriceDelta } : {}),
        ...(seed.phrasing ? { phrasing: seed.phrasing } : {}),
        ...(seed.name ? { name: seed.name } : {}),
        isNew: true,
      };
    },
    [seed],
  );

  const [draft, setDraft] = useState<BuilderDraft>(() => withSeed(blankDraft()));
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveDone, setSaveDone] = useState(false);
  const [triedSave, setTriedSave] = useState(false);

  const patch = (p: Partial<BuilderDraft>) => setDraft((d) => ({ ...d, ...p }));

  // Prefill on edit; reset on create. Categories come from the vertical config.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!ruleId) {
        setDraft(withSeed(blankDraft()));
        return;
      }
      try {
        const rules = await listRules();
        const r = rules.find((x) => x.id === ruleId);
        if (active && r) {
          setDraft({
            ...blankDraft(),
            id: r.id,
            name: r.name,
            motion: r.motion,
            priority: r.priority,
            select: r.select || 'curated_order',
            status: r.status,
            source: r.source,
            isNew: false,
          });
        }
      } catch (e) {
        handleApiError(e, 'Could not load the rule');
      }
    })();
    return () => {
      active = false;
    };
  }, [ruleId, vertical, withSeed]);

  useEffect(() => {
    getCapabilities()
      .then(setCaps)
      .catch(() => undefined);
  }, []);

  // Once the real catalog categories arrive, remap any draft category that
  // isn't a real one (e.g. the seed default 'mains') onto a real category id —
  // otherwise the dropdown shows a phantom value and the engine matches nothing.
  useEffect(() => {
    const real = caps?.cats;
    if (!real || real.length === 0) return;
    const valid = new Set(real.map((c) => c.value));
    const first = real[0].value;
    const second = real[1]?.value ?? first;
    setDraft((d) => {
      if (valid.has(d.triggerCat) && valid.has(d.fromValue) && valid.has(d.excludeCat)) return d;
      return {
        ...d,
        triggerCat: valid.has(d.triggerCat) ? d.triggerCat : first,
        excludeCat: valid.has(d.excludeCat) ? d.excludeCat : second,
        fromValue: valid.has(d.fromValue) ? d.fromValue : second,
      };
    });
  }, [caps]);

  // Debounced live preview on every draft change.
  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      previewRule(draft)
        .then((r) => active && setPreview(r))
        .catch(() => active && setPreview(null));
    }, 220);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [draft]);

  const strategyOptions: Option[] = (caps?.strategies ?? []).map((s) => ({
    value: s.key,
    label: s.available ? STRATEGY_LABELS[s.key] ?? s.key : `${STRATEGY_LABELS[s.key] ?? s.key} — ${s.reason ?? 'unavailable'}`,
    disabled: !s.available,
  }));

  const offStrategies = (caps?.strategies ?? []).filter((s) => !s.available);
  const capabilityNote = offStrategies.length
    ? `Capability-gated by your catalog: ${offStrategies
        .map((s) => `${STRATEGY_SHORT[s.key] ?? s.key} is off (${s.reason})`)
        .join('; ')}. attach-rate is unlocked — it has history.`
    : 'All pick strategies are available for your catalog.';

  // Validate + persist. Returns true on success; callers decide what UI follows
  // (a toast + navigate for drafts, a success dialog for activation).
  // Validate the draft; surface the first problem (e.g. missing name) as a toast
  // and flip on inline field errors. Returns true only when the rule is complete.
  const validate = async (): Promise<boolean> => {
    setTriedSave(true);
    try {
      await builderSchema.validate(draft);
      return true;
    } catch (err) {
      toast.error('Check the rule', err instanceof Error ? err.message : undefined);
      return false;
    }
  };

  const persistRule = async (activate: boolean): Promise<boolean> => {
    try {
      if (draft.isNew) await createRule(draft, activate);
      else await updateRule(draft.id as string, draft, activate);
      counts.refresh();
      return true;
    } catch (e) {
      handleApiError(e, 'Could not save the rule');
      return false;
    }
  };

  // Save draft → validate, then save.
  const onSaveDraft = async () => {
    if (!(await validate())) return;
    if (await persistRule(false)) {
      toast.info('Draft saved');
      nav.navigate('Rules');
    }
  };

  // Save & activate → validate FIRST; only open the confirm dialog if valid.
  const onSaveActivate = async () => {
    if (await validate()) setConfirmOpen(true);
  };

  const onConfirmActivate = async () => {
    if (await persistRule(true)) {
      setConfirmOpen(false);
      setSaveDone(true);
    }
  };

  const insertToken = (tok: string) => patch({ phrasing: `${draft.phrasing} ${tok}`.trim() });

  return (
    <Page>
      <Row gap={10} style={{ marginBottom: 4 }}>
        <Button label="Rules" variant="ghost" size="sm" icon="arrow-l" onPress={() => nav.navigate('Rules')} />
      </Row>

      <PageHeader
        icon="pencil"
        eyebrow="Authoring"
        title={draft.isNew ? 'New rule' : 'Edit rule'}
        subtitle="Compose your rule as a single, plain sentence. The engine reads it left to right and resolves it deterministically — the same way, every time."
        actions={
          <>
            <Button label="Save draft" onPress={onSaveDraft} />
            <Button label="Save & activate" icon="check" iconStroke={2.4} variant="accent" onPress={onSaveActivate} />
          </>
        }
        style={{ marginBottom: 22 }}
      />

      <View style={{ flexDirection: isDesktop ? 'row' : 'column', gap: 24, alignItems: 'flex-start' }}>
        {/* ── Form column ── */}
        <View style={{ flex: 1, minWidth: 0, gap: 18, alignSelf: 'stretch' }}>
          {/* Name / motion / priority */}
          <Card>
            <CardBody>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
                <View style={{ flex: 1, minWidth: 180, gap: 4 }}>
                  <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                    Rule name
                  </Txt>
                  <TextField
                    value={draft.name}
                    onChangeText={(v) => patch({ name: v })}
                    placeholder="Name this rule…"
                    style={triedSave && !draft.name.trim() ? { borderColor: C.danger } : undefined}
                  />
                  {triedSave && !draft.name.trim() && (
                    <Txt size={FS.xs} color={C.danger}>
                      Name is required
                    </Txt>
                  )}
                </View>
                <View style={{ width: 150, gap: 4 }}>
                  <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                    Motion
                  </Txt>
                  <Select value={draft.motion} onChange={(v) => patch({ motion: v })} options={MOTION_OPTIONS} />
                </View>
                <View style={{ width: 84, gap: 4 }}>
                  <Txt size={FS.sm} weight={FW.medium} color={C.ink2}>
                    Priority
                  </Txt>
                  <TextField value={String(draft.priority)} onChangeText={(v) => patch({ priority: Number(v) || 0 })} numeric />
                </View>
              </View>
            </CardBody>
          </Card>

          {/* The rule, in plain English */}
          <Card>
            <CardHead title="The rule, in plain English" right={<Chip tone="accent" label="guided" />} />
            <CardBody>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  columnGap: 8,
                  rowGap: 14,
                  paddingVertical: 4,
                }}
              >
                <SentenceText>When a cart contains</SentenceText>
                <Select variant="token" value={draft.triggerCat} onChange={(v) => patch({ triggerCat: v })} options={catOptions} />
                <SentenceText>and has no</SentenceText>
                <Select variant="token" value={draft.excludeCat} onChange={(v) => patch({ excludeCat: v })} options={catOptions} />
                <SentenceText>— suggest</SentenceText>
                <Select variant="token" value={draft.fromValue} onChange={(v) => patch({ fromValue: v })} options={catOptions} />
                <SentenceText>picked by</SentenceText>
                <Select variant="token" value={draft.select} onChange={(v) => patch({ select: v })} options={strategyOptions} />
                <SentenceText>, at most</SentenceText>
                <Select
                  variant="token"
                  value={String(draft.maxSuggestions)}
                  onChange={(v) => patch({ maxSuggestions: Number(v) })}
                  options={[
                    { value: '1', label: '1' },
                    { value: '2', label: '2' },
                  ]}
                />
                <SentenceText>item, within</SentenceText>
                <TokenNumber value={String(draft.maxPriceDelta)} onChangeText={(v) => patch({ maxPriceDelta: Number(v) || 0 })} />
                <SentenceText>% of the trigger price.</SentenceText>
              </View>
              <Alert tone="info" style={{ marginTop: 16 }}>
                <Txt size={13} color={C.info} lineHeight={18}>
                  {capabilityNote}
                </Txt>
              </Alert>
            </CardBody>
          </Card>

          {/* Guardrails */}
          <Card>
            <CardHead title="Guardrails" />
            <CardBody>
              <Stack gap={14}>
                <Switch
                  value={draft.mustAvailable}
                  onValueChange={(v) => patch({ mustAvailable: v })}
                  label="Only suggest items that are available right now (86 / stock-aware)"
                />
                <Switch value={draft.dietaryMatch} onValueChange={(v) => patch({ dietaryMatch: v })} label={cfg.compatLabel} />
              </Stack>
            </CardBody>
          </Card>

        </View>

        {/* ── Live preview + phrasing column ── */}
        <View style={{ flex: isDesktop ? 1 : undefined, minWidth: 0, alignSelf: 'stretch', gap: 18 }}>
          <PreviewPanel preview={preview} />

          {/* Phrasing — sits under the live preview */}
          <Card>
            <CardHead title="Phrasing" right={<Txt size={FS.xs} color={C.ink4}>screen template · SUKI carries voice</Txt>} />
            <CardBody>
              <Stack gap={12}>
                <TextField value={draft.phrasing} onChangeText={(v) => patch({ phrasing: v })} />
                <Cluster gap={6}>
                  <Txt size={FS.xs} color={C.ink4}>
                    Insert:
                  </Txt>
                  <Pressable onPress={() => insertToken('{item_name}')}>
                    <Chip><Txt mono size={FS.xs} color={C.ink2}>{'{item_name}'}</Txt></Chip>
                  </Pressable>
                  <Pressable onPress={() => insertToken('{trigger_item_name}')}>
                    <Chip><Txt mono size={FS.xs} color={C.ink2}>{'{trigger_item_name}'}</Txt></Chip>
                  </Pressable>
                </Cluster>
                <Row gap={6}>
                  <Txt size={FS.sm} color={C.ink3} style={{ marginRight: 4 }}>
                    Tone
                  </Txt>
                  {TONES.map((t) => (
                    <FilterChip key={t} label={t} selected={draft.tone === t} onPress={() => patch({ tone: t })} height={26} paddingX={12} />
                  ))}
                </Row>
              </Stack>
            </CardBody>
          </Card>
        </View>
      </View>

      {/* Confirm before going live */}
      <ConfirmDialog
        visible={confirmOpen}
        title="Save & activate this rule?"
        message="It will go live immediately and start making suggestions. You can edit or turn it off anytime."
        confirmText="Yes, activate"
        cancelText="Keep editing"
        confirmColor={C.accent}
        onConfirm={onConfirmActivate}
        onCancel={() => setConfirmOpen(false)}
        onClose={() => setConfirmOpen(false)}
      />

      {/* Success after it saves */}
      <SuccessDialog
        visible={saveDone}
        title="Rule saved & activated"
        message={`"${draft.name.trim() || 'Your rule'}" is now live.`}
        primaryAction={{ label: 'Done', onPress: () => { setSaveDone(false); nav.navigate('Rules'); } }}
        secondaryAction={{ label: 'Keep editing', onPress: () => setSaveDone(false) }}
        onClose={() => setSaveDone(false)}
      />
    </Page>
  );
}

const SentenceText: React.FC<{ children: string }> = ({ children }) => (
  <Txt size={16} color={C.ink2} lineHeight={30}>
    {children}
  </Txt>
);

function PreviewPanel({ preview }: { preview: PreviewResult | null }) {
  const eligible = preview?.candidate_pool.filter((c) => c.eligible) ?? [];
  const dropped = preview?.candidate_pool.filter((c) => !c.eligible) ?? [];

  return (
    <Card style={{ borderColor: C.accentLine }}>
      <CardHead
        accent
        title="Live preview"
        right={
          <Chip leading={<Spinner size={9} borderWidth={1.5} />} label="deterministic" style={{ backgroundColor: C.card }} />
        }
      />
      <CardBody>
        <Stack gap={14}>
          {/* Sample cart */}
          <View>
            <Txt size={FS.xs} weight={FW.semibold} color={C.ink4} transform="uppercase" tracking={0.6} style={{ marginBottom: 6 }}>
              Sample cart
            </Txt>
            <Cluster>
              {preview?.trigger ? (
                <Chip tone="neutral" dot lg label={`${preview.trigger.name} · ${fmtPrice(preview.trigger.price)}`} />
              ) : (
                <Chip tone="neutral" dot lg label="—" />
              )}
            </Cluster>
          </View>

          <Divider />

          {/* Candidate pool */}
          <View>
            <Txt size={FS.xs} weight={FW.semibold} color={C.ink4} transform="uppercase" tracking={0.6} style={{ marginBottom: 8 }}>
              Engine evaluates {preview?.candidate_pool.length ?? 0} candidates
            </Txt>
            <Stack gap={6}>
              {eligible.map((c, i) => {
                const picked = preview?.pick?.item_id === c.item_id && i === 0;
                return (
                  <View
                    key={c.item_id}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                      paddingVertical: 7,
                      paddingHorizontal: 10,
                      borderRadius: R.r2,
                      borderWidth: 1,
                      borderColor: picked ? C.successLine : C.line,
                      backgroundColor: picked ? C.successSoft : C.card,
                    }}
                  >
                    <Txt size={13} style={{ width: 16 }}>
                      {picked ? '★' : ''}
                    </Txt>
                    <Txt size={13} weight={picked ? FW.semibold : FW.regular} color={picked ? C.ink : C.ink2} style={{ flex: 1 }} numberOfLines={1}>
                      {c.name}
                    </Txt>
                    <Txt mono size={13} color={C.ink3}>
                      {fmtPrice(c.price)}
                    </Txt>
                  </View>
                );
              })}
              {dropped.map((c) => (
                <View
                  key={c.item_id}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    paddingVertical: 7,
                    paddingHorizontal: 10,
                    borderRadius: R.r2,
                    borderWidth: 1,
                    borderColor: C.line,
                    backgroundColor: C.paper2,
                    opacity: 0.55,
                  }}
                >
                  <Txt size={13} style={{ width: 16 }} />
                  <Txt size={13} color={C.ink4} style={{ flex: 1, textDecorationLine: 'line-through' }} numberOfLines={1}>
                    {c.name}
                  </Txt>
                  <Txt mono size={13} color={C.ink3}>
                    {fmtPrice(c.price)}
                  </Txt>
                  {c.drop_reason && (
                    <Txt size={11.5} color={C.danger}>
                      {DROP_LABEL[c.drop_reason]}
                    </Txt>
                  )}
                </View>
              ))}
            </Stack>
          </View>

          {/* Deterministic pick / no pick */}
          {preview?.pick ? (
            <View style={{ backgroundColor: C.successSoft, borderWidth: 1, borderColor: C.successLine, borderRadius: R.r3, padding: 12 }}>
              <Row gap={6} style={{ marginBottom: 6 }}>
                <Icon name="star-fill" size={13} color={C.success} />
                <Txt size={FS.xs} weight={FW.semibold} color={C.success} transform="uppercase" tracking={0.6}>
                  Deterministic pick
                </Txt>
              </Row>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Txt size={16} weight={FW.semibold} color={C.ink}>
                  {preview.pick.name}
                </Txt>
                <Txt mono size={14} color={C.ink2}>
                  {fmtPrice(preview.pick.price)}
                </Txt>
              </View>
              <Txt size={13} color={C.ink2} style={{ marginTop: 6 }}>
                {preview.pick_reason}
              </Txt>
              <View style={{ marginTop: 10, paddingVertical: 9, paddingHorizontal: 11, backgroundColor: C.card, borderWidth: 1, borderColor: C.successLine, borderRadius: R.r2 }}>
                <Txt size={14} italic color={C.ink}>
                  “{preview.pick.phrasing}”
                </Txt>
              </View>
            </View>
          ) : (
            <Alert tone="warn" icon={null}>
              <Txt size={13} color={C.warn}>
                No eligible candidate — the engine returns empty and stays silent. The order is never blocked.
              </Txt>
            </Alert>
          )}

          {/* Billing footnote */}
          <Row gap={6} style={{ alignItems: 'flex-start' }}>
            <View style={{ marginTop: 1 }}>
              <Icon name="lock" size={13} color={C.ink4} />
            </View>
            <Txt size={FS.xs} color={C.ink4} style={{ flex: 1 }} lineHeight={16}>
              Engine returns the item ID only. SK Billing computes price + GST on accept.
            </Txt>
          </Row>
        </Stack>
      </CardBody>
    </Card>
  );
}
